let container
let wrapper
let content
let controls
let nextBtn
let skipBtn

let skipPromise
let focusElement
let arrow
let skipHandler

export const getContainer = () => container || createContainer()
export const getWrapper = () => wrapper || createWrapper()
export const getContent = () => content || createContent()
export const getControls = () => controls || createControls()
export const getNextBtn = () => nextBtn || createNextBtn()
export const getSkipBtn = () => skipBtn || createSkipBtn()
export const getArrow = () => arrow || createArrow()

function createContainer() {
    container = document.createElement('div')
    container.id = 'tutorial-container'
    document.body.append(container)
    return container
}

function createWrapper() {
    wrapper = document.createElement('div')
    wrapper.classList.add('tutorial-wrapper')
    getContainer().append(wrapper)
    return wrapper
}

function createContent() {
    content = document.createElement('div')
    content.classList.add('tutorial-content')
    getWrapper().prepend(content)
    return content
}

function createControls() {
    controls = document.createElement('div')
    controls.classList.add('tutorial-controls')
    getWrapper().append(controls)
    return controls
}

function createNextBtn() {
    nextBtn = document.createElement('button')
    nextBtn.classList.add('tutorial-next')
    getControls().prepend(nextBtn)
    return nextBtn
}

function createSkipBtn() {
    skipBtn = document.createElement('button')
    skipBtn.classList.add('tutorial-skip')
    skipPromise = new Promise(resolve => {
        const handleSkipClick = e => {
            skipBtn.removeEventListener('click', handleSkipClick)
            resolve('skip')
            removeFocus()
            document.body.removeChild(container)
            if (skipHandler) skipHandler(e)
            container = wrapper = content = controls = nextBtn = skipBtn = skipPromise = skipHandler = null
            document.body.style.overflow = ''
        }
        skipBtn.addEventListener('click', handleSkipClick)
    })
    getControls().append(skipBtn)
    return skipBtn
}

function createArrow() {
    arrow = document.createElement('div')
    arrow.classList.add('tutorial-arrow')
    getContainer().append(arrow)
    return arrow
}

function adjustScrollPosition() {
    const { top, bottom } = focusElement.getBoundingClientRect()
    const bottomDistance = window.innerHeight - bottom

    if (top < 0 || (top < bottomDistance && bottomDistance < window.innerHeight / 2)) {
        focusElement.scrollIntoView()
        window.scrollBy(0, -50)
    } else if (bottomDistance < 0 || (top > bottomDistance && top < window.innerHeight / 2)) {
        focusElement.scrollIntoView(false)
        window.scrollBy(0, 50)
    }

    adjustScrollPositionToWrapper()
}

function adjustScrollPositionToWrapper() {
    const { top, bottom } = wrapper.getBoundingClientRect()
    const bottomDistance = window.innerHeight - bottom
    if (top < 0) {
        window.scrollBy(0, top)
        wrapper.style.bottom = 'auto'
        wrapper.style.top = 0
    } else if (bottomDistance < 0) {
        window.scrollBy(0, -bottomDistance)
        wrapper.style.bottom = 0
        wrapper.style.top = 'auto'
    }
    placeArrow()
}

function placeWrapper() {
    const boundingBox = focusElement.getBoundingClientRect()
    const MARGIN = 150
    let style = 'position: absolute;'
    // Above or below focus element:
    if (window.innerHeight - boundingBox.bottom > boundingBox.top) {
        const top = boundingBox.bottom + MARGIN
        style += ' top: ' + top + 'px;'
    } else {
        const bottom = window.innerHeight - boundingBox.top + MARGIN
        style += ' bottom: ' + bottom + 'px;'
    }
    // Move horizontally if necessary
    const currentBounds = wrapper.getBoundingClientRect()
    if (boundingBox.left > currentBounds.right) {
        const left = boundingBox.right - wrapper.offsetWidth
        style += ' left: ' + left || 0 + 'px; margin: 0;'
    } else if (boundingBox.right < currentBounds.left) {
        style += ' left: ' + boundingBox.left || 0 + 'px; margin: 0;'
    }
    wrapper.setAttribute('style', style)
    return wrapper.getBoundingClientRect()
}

function placeArrow() {
    const focusBoundingBox = focusElement.getBoundingClientRect()
    const wrapperBoundingBox = wrapper.getBoundingClientRect()
    const wrapperCenterX = (wrapperBoundingBox.left + wrapperBoundingBox.right) / 2
    const wrapperCenterY = (wrapperBoundingBox.top + wrapperBoundingBox.bottom) / 2
    const top = (wrapperCenterY > focusBoundingBox.bottom) ? focusBoundingBox.bottom + 30 : focusBoundingBox.top - 30
    const left = (focusBoundingBox.left + focusBoundingBox.right) / 2 - 15
    const vectorX = wrapperCenterX - left
    const vectorY = wrapperCenterY - top
    const angle = Math.atan2(vectorX, vectorY) * -180 / Math.PI
    getArrow().setAttribute('style', 'left: ' + left + 'px; top: ' + top + 'px; transform: rotate(' + angle + 'deg)')
}

export async function startTutorial(tutorial) {
    const tutorialGenerator = tutorial()
    document.body.style.overflow = 'hidden'
    let actionListener = tutorialGenerator.next().value
    while (actionListener) {
        const action = await Promise.race([actionListener(), skipPromise])
        if (action === 'skip') break
        actionListener = tutorialGenerator.next().value
    }
}

export function setContent(newContent) {
    // Wrap plain text with <p> (very simple check but serves our needs)
    const htmlContent = (newContent.trim().startsWith('<')) ? newContent : '<p>' + newContent + '</p>'
    getContent().innerHTML = htmlContent
    if (focusElement) adjustScrollPositionToWrapper()
}

export function showNextBtn(contentHTML) {
    getNextBtn().innerHTML = contentHTML
}

/**
 * Show a skip button in the tutorial.
 * @param {String} contentHTML HTML to render inside the skip button
 * @param {function} [onSkip] Handler, called when the skip button is clicked
 */
export function showSkipBtn(contentHTML, onSkip) {
    getSkipBtn().innerHTML = contentHTML
    skipHandler = onSkip
}

export function focus(selector) {
    if (focusElement) focusElement.classList.remove('tutorial-focus')
    focusElement = document.querySelector(selector)
    getWrapper().setAttribute('style', '')
    if (!focusElement) {
        container.classList.remove('tutorial-has-focus')
        if (arrow) {
            container.removeChild(arrow)
            arrow = null
        }
        return
    }
    focusElement.classList.add('tutorial-focus')
    container.classList.add('tutorial-has-focus')

    adjustScrollPosition()
    placeWrapper()
}

export function removeFocus() {
    if (!focusElement) return
    focusElement.classList.remove('tutorial-focus')
    getContainer().classList.remove('tutorial-has-focus')
    focusElement = null
    getWrapper().setAttribute('style', '')
    if (arrow) {
        container.removeChild(arrow)
        arrow = null
    }
}

export function removeNextBtn() {
    controls.removeChild(nextBtn)
    nextBtn = null
}

export function removeSkipBtn() {
    controls.removeChild(skipBtn)
    skipBtn = null
}
